import { db } from '../firebase';
import {
  doc, setDoc, getDoc,
  collection, addDoc, getDocs, deleteDoc,
  serverTimestamp
} from 'firebase/firestore';

export { serverTimestamp };

const TODAY = () => new Date().toISOString().split('T')[0];

// ── USER PROFILE ──────────────────────────────────────────────

export const saveUserProfile = async (userId, data) => {
  await setDoc(doc(db, 'users', userId), data, { merge: true });
};

export const getUserProfile = async (userId) => {
  const snap = await getDoc(doc(db, 'users', userId));
  return snap.exists() ? snap.data() : null;
};

export const deleteUserProfile = async (userId) => {
  await deleteDoc(doc(db, 'users', userId));
};

export const getAllUsers = async () => {
  const [usersSnap, subsSnap] = await Promise.all([
    getDocs(collection(db, 'users')),
    getDocs(collection(db, 'subscriptions'))
  ]);
  const subsMap = {};
  subsSnap.forEach(d => { subsMap[d.id] = d.data(); });
  return usersSnap.docs.map(d => ({
    id: d.id,
    ...d.data(),
    subscriptionTier: subsMap[d.id]?.planName || d.data().subscriptionTier || 'free',
    subscriptionEndDate: subsMap[d.id]?.endDate || null,
    subscriptionStatus: subsMap[d.id]?.status || null,
  }));
};

// ── TRADES (subcollection: users/{userId}/trades) ─────────────

export const saveTrade = async (userId, tradeData) => {
  const tradesRef = collection(db, 'users', userId, 'trades');
  const docRef = await addDoc(tradesRef, tradeData);
  return docRef.id;
};

export const getTrades = async (userId) => {
  const tradesRef = collection(db, 'users', userId, 'trades');
  const snap = await getDocs(tradesRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const deleteTrade = async (userId, tradeId) => {
  await deleteDoc(doc(db, 'users', userId, 'trades', tradeId));
};

// ── SUBSCRIPTIONS (top-level: subscriptions/{userId}) ─────────

export const saveSubscription = async (userId, subscriptionData) => {
  await setDoc(doc(db, 'subscriptions', userId), subscriptionData);
};

export const getSubscription = async (userId) => {
  if (!userId) return null;
  const snap = await getDoc(doc(db, 'subscriptions', userId));
  return snap.exists() ? snap.data() : null;
};

// ── MESSAGE COUNT (top-level: userMessageCount/{userId}) ─────
// Document: { count: number, date: "YYYY-MM-DD" }
// count resets automatically when date changes (new day)

export const getTodayMessageCount = async (userId) => {
  if (!userId) return 0;
  const snap = await getDoc(doc(db, 'userMessageCount', userId));
  if (!snap.exists()) return 0;
  const data = snap.data();
  return data.date === TODAY() ? (data.count || 0) : 0;
};

export const setMessageCount = async (userId, count) => {
  await setDoc(doc(db, 'userMessageCount', userId), { count, date: TODAY() });
};

// ── STREAK (subcollection: users/{userId}/streak/data) ────────
// Structure: { currentStreak, lastTradeDate, longestStreak }
// Increments by 1 each calendar day the user logs at least 1 trade.
// Resets to 1 if a day is skipped.

export const getStreak = async (userId) => {
  if (!userId) return null;
  const snap = await getDoc(doc(db, 'users', userId, 'streak', 'data'));
  return snap.exists() ? snap.data() : null;
};

export const updateStreak = async (userId) => {
  const today = TODAY();
  const streakRef = doc(db, 'users', userId, 'streak', 'data');
  const snap = await getDoc(streakRef);

  if (!snap.exists()) {
    const initial = { currentStreak: 1, lastTradeDate: today, longestStreak: 1 };
    await setDoc(streakRef, initial);
    return initial;
  }

  const data = snap.data();
  // Support legacy field name 'lastLoginDate' for existing users
  const last = data.lastTradeDate || data.lastLoginDate;

  if (last === today) {
    // Already logged a trade today — no change
    return data;
  }

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  const newStreak = last === yesterdayStr ? data.currentStreak + 1 : 1;
  const longestStreak = Math.max(newStreak, data.longestStreak || 1);
  const updated = { currentStreak: newStreak, lastTradeDate: today, longestStreak };
  await setDoc(streakRef, updated);
  return updated;
};

// ── AI INSIGHTS (subcollection: users/{userId}/aiInsights) ───────────────
// Each doc: { tradeId, stock, pnl, emotion, insights: [...], timestamp }

export const saveAiInsight = async (userId, insightData) => {
  const ref = collection(db, 'users', userId, 'aiInsights');
  await addDoc(ref, { ...insightData, timestamp: serverTimestamp() });
};

// ── WEEKLY STATS (subcollection: users/{userId}/weeklyStats/{weekKey}) ──
// Structure: { totalTrades, winRate, psychScore, topMistake }

export const getWeeklyStats = async (userId, weekKey) => {
  if (!userId || !weekKey) return null;
  const snap = await getDoc(doc(db, 'users', userId, 'weeklyStats', weekKey));
  return snap.exists() ? snap.data() : null;
};

export const saveWeeklyStats = async (userId, weekKey, stats) => {
  await setDoc(doc(db, 'users', userId, 'weeklyStats', weekKey), stats);
};

// ── MONTHLY STATS (subcollection: users/{userId}/monthlyStats/{YYYY-MM}) ──
// Structure: { month, totalTrades, winRate, psychologyScore, topMistake, longestStreak, emotionalLossAmount, emotionalTradeCount }

export const getMonthlyStats = async (userId, monthKey) => {
  if (!userId || !monthKey) return null;
  const snap = await getDoc(doc(db, 'users', userId, 'monthlyStats', monthKey));
  return snap.exists() ? snap.data() : null;
};

export const saveMonthlyStats = async (userId, monthKey, stats) => {
  await setDoc(doc(db, 'users', userId, 'monthlyStats', monthKey), stats);
};

// ── REPORT META (users/{userId}/reports/meta) ─────────────────
// Tracks last generated dates to prevent duplicate reports
// Fields: { lastWeeklyReport: "YYYY-Www", lastMonthlyReport: "YYYY-MM" }

export const getReportMeta = async (userId) => {
  if (!userId) return {};
  const snap = await getDoc(doc(db, 'users', userId, 'reports', 'meta'));
  return snap.exists() ? snap.data() : {};
};

export const saveReportMeta = async (userId, data) => {
  await setDoc(doc(db, 'users', userId, 'reports', 'meta'), data, { merge: true });
};

// ── RISK SETTINGS (top-level: riskSettings/{userId}) ──────────

export const saveRiskSettings = async (userId, settings) => {
  await setDoc(doc(db, 'riskSettings', userId), settings);
};

export const getRiskSettings = async (userId) => {
  if (!userId) return null;
  const snap = await getDoc(doc(db, 'riskSettings', userId));
  return snap.exists() ? snap.data() : null;
};

// ── RULE VIOLATIONS (subcollection: users/{userId}/ruleViolations) ──────────
// Each doc: { rule, title, message, actual, limit, action, timestamp, date }

export const saveRuleViolation = async (userId, violation) => {
  if (!userId) return;
  const ref = collection(db, 'users', userId, 'ruleViolations');
  await addDoc(ref, {
    ...violation,
    date: new Date().toISOString().split('T')[0],
    timestamp: serverTimestamp()
  });
};

export const getWeeklyViolations = async (userId) => {
  if (!userId) return [];
  const ref = collection(db, 'users', userId, 'ruleViolations');
  const snap = await getDocs(ref);
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(v => {
      const ts = v.timestamp?.toDate ? v.timestamp.toDate() : (v.date ? new Date(v.date) : null);
      return ts && ts >= weekAgo;
    });
};
